/**
 * What a ticket may show, given how it was loaded.
 *
 * Offline tickets and live status pull in opposite directions, and this is where that is
 * resolved rather than in the middle of the markup.
 *
 * A booking has two kinds of content. The boarding codes, seats, passengers and route are
 * settled the moment the booking is confirmed — they are safe to show from a cache of any
 * age, and showing them is the entire point of an offline ticket. The trip's STATUS is not:
 * delay, cancellation and the operator's note all change after the booking is made, and
 * they are the fields a passenger acts on. A cached "on time" presented as current is worse
 * than showing nothing, because it is a specific claim about right now that nobody checked.
 *
 * So: codes from cache, status never.
 */

export interface TicketLoad {
  /** The service worker served this from cache — see the `x-busbook-cached` stamp in sw.ts. */
  cached: boolean;
  /** When the server produced the cached copy, from its `date` header. Null if absent. */
  servedAt: string | null;
  /** The operator shows live trip status. */
  liveStatus: boolean;
  /** The operator allows a ticket to be used offline. */
  offlineTicket: boolean;
}

export interface TicketPresentation {
  /** Render delay / cancellation / note. */
  showStatus: boolean;
  /** Render the boarding QR. */
  showCode: boolean;
  /**
   * `asOf`  — offline, and we can say when this was last known.
   * `stale` — offline, with no usable timestamp.
   * `none`  — live.
   */
  notice: 'none' | 'asOf' | 'stale';
}

export function ticketPresentation(load: TicketLoad): TicketPresentation {
  if (!load.cached) {
    return { showStatus: load.liveStatus, showCode: true, notice: 'none' };
  }

  return {
    // Never from cache, whatever the operator has configured. This is not a feature switch
    // — it is the difference between reporting and guessing.
    showStatus: false,
    // The operator's switch decides only whether the CODE survives going offline. Off means
    // the service worker must not quietly deliver the feature they turned off.
    showCode: load.offlineTicket,
    notice: parseable(load.servedAt) ? 'asOf' : 'stale',
  };
}

/** A `date` header we cannot parse is worse than none: it would print "Invalid Date". */
function parseable(value: string | null): value is string {
  return value !== null && Number.isFinite(Date.parse(value));
}
